import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getZeroOption, type Option, Constants, getValidSelectValue } from '@/src/utils';
import {
    type InitialOptions,
    type ClassOption,
    type SchoolClassOptions,
    type ExamOption,
    type FilterState,
} from '../models/filterDTOs';
import {
    ErrorMessage,
    SimpleButton,
    SimpleSingleSelect,
    FilterIcon,
    ICON_COLORS,
    FilterBarContainer,
} from '@/src/ui';

interface FiltersProps {
    refreshTableData: (filters: FilterState) => void;
    filterState: FilterState;
    setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
    setSearchString: (val: string) => void;
}

const requiredFields = ['districtId', 'schoolId', 'gradeLevelMasterId'];

function getMissingFields(fs: any): string[] {
    return requiredFields
        .filter((key) => !fs[key] || Number(fs[key]) === 0)
        .map((key) => Constants.fieldLabels[key]);
}

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const SelectDistrictOption = getZeroOption('Select District');
const SelectSchoolOption = getZeroOption('Select School');
const SelectGradeOption = getZeroOption('Select Grade');
const NoClassOption = getZeroOption('No Classes Available');
const NoExamOption = getZeroOption('No Exams Available');

const Filters: React.FC<FiltersProps> = ({ refreshTableData, filterState, setFilterState }) => {
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([SelectSchoolOption]);
    const [classOptions, setClassOptions] = useState<Option[]>([AllOption]);
    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Option[]>([
        LoadingOption,
    ]);
    const [examTypeMasterOptions, setExamTypeMasterOptions] = useState<Option[]>([AllOption]);
    const [examOptions, setExamOptions] = useState<Option[]>([AllOption]);
    const [allExams, setAllExams] = useState<ExamOption[]>([]);
    const [filtersLoading, setFiltersLoading] = useState(true);
    const getLoadingProps = (opts: Option[], val: number, loading: boolean) =>
        loading
            ? { options: [LoadingOption], value: 0, disabled: true }
            : { options: opts, value: val };

    const [classData, setClassData] = useState<ClassOption[]>([]);
    const [fieldsChanged, setFieldsChanged] = useState(false);

    const filterExamsLocally = (newFilterState: FilterState, allExamsData: ExamOption[]) => {
        const { schoolId, classId, examTypeMasterId } = newFilterState;
        let filtered = [...allExamsData];

        filtered = filtered.filter((exam) => exam.schoolIds && exam.schoolIds.length > 0);

        if (schoolId && schoolId !== Number(AllOption.id)) {
            filtered = filtered.filter((exam) => exam.schoolIds.includes(schoolId));
        }

        if (classId && classId !== Number(AllOption.id)) {
            filtered = filtered.filter(
                (exam) => !exam.classIds || !exam.classIds.length || exam.classIds.includes(classId)
            );
        }

        if (examTypeMasterId && examTypeMasterId !== Number(AllOption.id)) {
            filtered = filtered.filter((exam) => exam.examTypeMasterId === examTypeMasterId);
        }

        if (!filtered.length) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
            return;
        }

        const sorted = filtered.sort((a, b) => a.name.localeCompare(b.name));
        setExamOptions([AllOption, ...sorted]);
        setFilterState((prev) => ({ ...prev, examId: Number(AllOption.id) }));
    };

    const maybeFetchExamOptions = async (newFilterState: FilterState) => {
        const { gradeLevelMasterId } = newFilterState;

        // Only fetch exams if a grade is selected
        if (!gradeLevelMasterId || gradeLevelMasterId === Number(SelectGradeOption.id)) {
            setExamOptions([AllOption]);
            setAllExams([]);
            setFilterState((prev) => ({ ...prev, examId: Number(AllOption.id) }));
            return;
        }

        try {
            const res = await apiRequest<{ examOptions: ExamOption[] }>(
                'GET',
                `utility/filterData?getExam=true&gradeLevelMasterIds=${gradeLevelMasterId}&showPublishedExamsOnly=true`
            );
            const data = res.data.examOptions || [];

            const filtered = data.filter((exam) => exam.schoolIds && exam.schoolIds.length > 0);
            const sorted = filtered.sort((a, b) => a.name.localeCompare(b.name));

            setAllExams(sorted);
            filterExamsLocally(newFilterState, sorted);
        } catch {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
            setErrorMsg('Failed to load exam options');
        }
    };

    const updateClassOptions = (
        data: ClassOption[],
        gradeLevelMasterId: number,
        schoolId: number
    ) => {
        if (
            !schoolId ||
            schoolId === Number(SelectSchoolOption.id) ||
            !gradeLevelMasterId ||
            gradeLevelMasterId === Number(SelectGradeOption.id)
        ) {
            setClassOptions([AllOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(AllOption.id) }));
            return;
        }

        const newClassOptions = data
            .filter(
                (option) =>
                    option.schoolId === schoolId &&
                    (!option.gradeLevelMasterIds?.length ||
                        option.gradeLevelMasterIds.includes(gradeLevelMasterId))
            )
            .map((option) => ({ id: option.id, name: option.name }));

        if (!newClassOptions.length) {
            setClassOptions([NoClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
        } else {
            setClassOptions([AllOption, ...newClassOptions]);

            setFilterState((prev) => {
                const classExists = newClassOptions.some(
                    (opt) => Number(opt.id) === Number(prev.classId)
                );
                if (classExists) {
                    return prev;
                }
                return {
                    ...prev,
                    classId: Number(AllOption.id),
                };
            });
        }
    };

    const fetchSchoolClassOptions = async (districtId: number | string) => {
        if (!districtId || Number(districtId) === Number(AllOption.id)) {
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([AllOption]);
            setFilterState((prev) => ({
                ...prev,
                schoolId: Number(AllOption.id),
                gradeLevelMasterId: Number(SelectGradeOption.id),
                classId: Number(AllOption.id),
            }));
            return;
        }

        try {
            const res = await apiRequest<SchoolClassOptions>(
                'GET',
                `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
            );
            const data = res.data;
            const newSchoolOptions = data.schoolOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setSchoolOptions([SelectSchoolOption, ...newSchoolOptions]);

            setFilterState((prev) => {
                const schoolExists = newSchoolOptions.some(
                    (opt) => Number(opt.id) === Number(prev.schoolId)
                );
                return {
                    ...prev,
                    schoolId: schoolExists ? prev.schoolId : Number(SelectSchoolOption.id),
                };
            });
            const sortedClassOptions = data.classOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setClassData(sortedClassOptions);
            setClassOptions([
                AllOption,
                ...sortedClassOptions.map((option) => ({ id: option.id, name: option.name })),
            ]);
            setFilterState((prev) => {
                const classExists = sortedClassOptions.some(
                    (opt) => Number(opt.id) === Number(prev.classId)
                );
                return {
                    ...prev,
                    classId: classExists ? prev.classId : Number(AllOption.id),
                };
            });
        } catch {
            setErrorMsg('Failed to fetch school and class options');
            setSchoolOptions([AllOption]);
            setClassOptions([AllOption]);
        }
    };

    useEffect(() => {
        if (
            filterState &&
            filterState.schoolId &&
            filterState.schoolId !== Number(SelectSchoolOption.id) &&
            filterState.gradeLevelMasterId &&
            filterState.gradeLevelMasterId !== Number(SelectGradeOption.id)
        ) {
            maybeFetchExamOptions(filterState);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.schoolId, filterState.classId, filterState.gradeLevelMasterId]);

    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true&getExamTypeMaster=true'
                );
                const data = res.data;

                setGradeLevelMasterOptions([SelectGradeOption, ...data.gradeLevelMasterOptions]);
                setDistrictOptions([
                    SelectDistrictOption,
                    ...data.districtOptions.sort((a, b) => a.name.localeCompare(b.name)),
                ]);
                setExamTypeMasterOptions([
                    AllOption,
                    ...data.examTypeMasterOptions.sort((a, b) => a.name.localeCompare(b.name)),
                ]);

                if (
                    filterState &&
                    filterState.districtId &&
                    filterState.districtId !== Number(SelectDistrictOption.id)
                ) {
                    await fetchSchoolClassOptions(filterState.districtId);
                    setFiltersLoading(false);
                } else {
                    setFiltersLoading(false);
                }
            } catch {
                setErrorMsg('Failed to fetch initial filter options');
                setFiltersLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            filterState &&
            filterState.schoolId &&
            filterState.schoolId !== Number(SelectSchoolOption.id) &&
            filterState.gradeLevelMasterId &&
            filterState.gradeLevelMasterId !== Number(SelectGradeOption.id) &&
            classData.length > 0
        ) {
            updateClassOptions(classData, filterState.gradeLevelMasterId, filterState.schoolId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classData, filterState.schoolId, filterState.gradeLevelMasterId]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                justifyContent: 'center',
                mb: 2,
            }}
        >
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...getLoadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newDistrictId = Number(e.target.value);
                        const newState = {
                            ...filterState,
                            districtId: newDistrictId,
                            schoolId: Number(SelectSchoolOption.id),
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(AllOption.id),
                        };
                        setFilterState(newState);
                        fetchSchoolClassOptions(newDistrictId);
                        setExamOptions([AllOption]);
                        setAllExams([]);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School *"
                    {...getLoadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newSchoolId = Number(e.target.value);
                        const newState = {
                            ...filterState,
                            schoolId: newSchoolId,
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(AllOption.id),
                        };
                        setFilterState(newState);
                        updateClassOptions(classData, Number(SelectGradeOption.id), newSchoolId);
                        setExamOptions([AllOption]);
                        setAllExams([]);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={filtersLoading || filterState.districtId === Number(AllOption.id)}
                />
                <SimpleSingleSelect
                    name="grade-select"
                    label="Grade Level *"
                    {...getLoadingProps(
                        gradeLevelMasterOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeLevelMasterOptions,
                                gradeLevelMasterOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newGradeLevelMasterId = Number(e.target.value);
                        const newState = {
                            ...filterState,
                            gradeLevelMasterId: newGradeLevelMasterId,
                        };
                        setFilterState(newState);
                        updateClassOptions(classData, newGradeLevelMasterId, newState.schoolId);
                        maybeFetchExamOptions(newState);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.schoolId === Number(SelectSchoolOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class"
                    {...getLoadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newClassId = Number(e.target.value);
                        const newState = { ...filterState, classId: newClassId };
                        setFilterState(newState);
                        filterExamsLocally(newState, allExams);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        filterState.schoolId === Number(SelectSchoolOption.id) ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
            </FilterBarContainer>

            <FilterBarContainer>
                <SimpleSingleSelect
                    name="exam-type-select"
                    label="Type"
                    value={filterState.examTypeMasterId}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newExamTypeMasterId = Number(e.target.value);
                        const newState = { ...filterState, examTypeMasterId: newExamTypeMasterId };
                        setFilterState(newState);
                        filterExamsLocally(newState, allExams);
                    }}
                    options={examTypeMasterOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />

                <SimpleSingleSelect
                    name="exam-select"
                    label="Name"
                    value={filterState.examId}
                    onChange={(e) =>
                        setFilterState((prev) => ({ ...prev, examId: Number(e.target.value) }))
                    }
                    options={examOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleButton
                    onClick={() => {
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setErrorMsg(`Please select: ${missing.join(', ')} before filtering.`);
                            return;
                        }
                        setErrorMsg(null);
                        setFieldsChanged(false);
                        refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />{' '}
                    Filter
                </SimpleButton>
            </FilterBarContainer>

            <ErrorMessage error={errorMsg} />
        </Box>
    );
};

export default Filters;
